import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useFabricationStore } from '../stores/fabricationStore';
import SourcePanel from '../components/persona_forge/SourcePanel';
import CockpitPanel from '../components/persona_forge/CockpitPanel';
import ArtifactPanel from '../components/persona_forge/ArtifactPanel';
import ProbesPanel from '../components/persona_forge/ProbesPanel';
import CapabilitiesPanel from '../components/persona_forge/CapabilitiesPanel';
import MindMapPanel from '../components/persona_forge/MindMapPanel';
import ForgeHeader from '../components/persona_forge/ForgeHeader';
import SaveDialog, { SaveDialogRef } from '../components/persona_forge/SaveDialog';
import TestDrivePanel from '../components/persona_forge/TestDrivePanel';
import { Spinner } from '../components/common/Spinner';
import { FabricationStage } from '../types/persona_engine';
import { useParams, useLocation, useNavigate, useSearchParams, useBlocker, Link } from 'react-router-dom';
import { draftBoardService } from '../services/draftBoardService';
import { FiArrowLeft, FiPlus, FiCode, FiSliders, FiFileText, FiTarget, FiZap, FiMap, FiBookOpen } from 'react-icons/fi';
import SkillsPanel from '../components/persona_forge/SkillsPanel';
import LineagePanel from '../components/lineage/LineagePanel';
import { FiGitBranch } from 'react-icons/fi';
import * as personaService from '../services/personaService';

const PersonaForge: React.FC = () => {
  const { darkMode } = useTheme();
  const {
    state, isLoading, error, init, loadPersona, remixPersona,
    resetState, isDirty, auto_mode, needs_recompile, runCompile,
    autoSaveEnabled, autoSaveStatus, lastAutoSavedAt,
    setAutoSaveEnabled, triggerAutoSave,
    currentPersonaId,
  } = useFabricationStore();
  const [intent, setIntent] = useState('');
  const [showIntentForm, setShowIntentForm] = useState(true);
  const [preflightIntent, setPreflightIntent] = useState<string | null>(null);
  
  // Helper function to reset all relevant state
  const resetForgeState = () => {
    resetState(); // Reset the store state
    setIntent(''); // Reset the intent text
    setShowIntentForm(true);
  };
  const { personaId: routePersonaId } = useParams<{ personaId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryPersonaId = searchParams.get('personaId');
  const remixPersonaId = location.state?.remixPersonaId;
  const [operationAttempted, setOperationAttempted] = useState(false);
  const [initializationComplete, setInitializationComplete] = useState(false);
  const [showNewConfirmation, setShowNewConfirmation] = useState(false);
  const [showNavigationConfirmation, setShowNavigationConfirmation] = useState(false);
  const [isRemixLoading, setIsRemixLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showRecompilePrompt, setShowRecompilePrompt] = useState(false);
  const [showNavBlockerModal, setShowNavBlockerModal] = useState(false);
  const [showSaveBeforeProbesModal, setShowSaveBeforeProbesModal] = useState(false);
  const [saveToast, setSaveToast] = useState(false);
  const saveDialogRef = useRef<SaveDialogRef>(null);
  // Tracks whether the current save was triggered by the probes-gate modal
  const saveIntentRef = useRef<'probes' | null>(null);

  // Block React Router navigation when there are unsaved changes.
  // Autosave only protects existing personas (needs an ID to PATCH against);
  // a freshly-generated but never-saved persona has currentPersonaId === null
  // and must be blocked even when autosave is on.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      !(autoSaveEnabled && currentPersonaId) &&
      currentLocation.pathname !== nextLocation.pathname
  );

  // When blocker fires, show our modal
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowNavBlockerModal(true);
    }
  }, [blocker.state]);
  
  // Combine both sources, prioritizing route params
  const personaId = routePersonaId || queryPersonaId;

  // Check if we're in new mode from URL parameter
  const isNewFromURL = searchParams.get('new') === 'true';

  // Determine if we're in remix mode based on state
  const isRemixMode = !!remixPersonaId;
  const isEditMode = !!personaId && !isRemixMode;
  // Read draft_id each render — useRef would freeze the initial value and miss
  // query-param-only navigations (same component instance is reused by React Router).
  const draftId = searchParams.get('draft_id');
  const isDraftMode = !!draftId && !personaId && !isRemixMode;
  // Draft mode is a fresh-session flavor of new mode — same reset semantics.
  const isNewMode = ((!isEditMode && !isRemixMode) || isNewFromURL) && !isDraftMode;

  // URL-level session identity. When this changes within a single mounted
  // PersonaForge (React Router reuses the instance across query-only nav),
  // we re-arm the entry effects so the new mode re-initializes correctly.
  const sessionKey = isRemixMode
    ? `remix:${remixPersonaId ?? ''}`
    : isEditMode
      ? `edit:${personaId ?? ''}`
      : isDraftMode
        ? `draft:${draftId ?? ''}`
        : 'new';
  const prevSessionKeyRef = useRef<string>(sessionKey);
  useEffect(() => {
    if (prevSessionKeyRef.current !== sessionKey) {
      prevSessionKeyRef.current = sessionKey;
      setInitializationComplete(false);
    }
  }, [sessionKey]);

  // Reset-on-entry for both new mode and draft mode. Draft mode always starts
  // from a clean store so prior persona state can't bleed into the new forge
  // session. useBlocker upstream already enforces save-or-discard on navigation
  // away from a dirty persona, so this reset is non-destructive by contract.
  useEffect(() => {
    if ((isNewMode || isDraftMode) && !initializationComplete) {
      resetForgeState();

      // Clean up the URL if needed
      if (isNewFromURL) {
        navigate('/personas/forge', { replace: true });
      }

      // Draft mode: finish init inside the draft-load effect below so the
      // intent pre-fill lands in the same tick as initializationComplete.
      if (!isDraftMode) {
        setInitializationComplete(true);
      }
    }
  }, [isNewMode, isDraftMode, isNewFromURL, initializationComplete, resetState, navigate]);
  
  // Handle remix loading if in remix mode
  useEffect(() => {
    if (isRemixMode && (personaId || remixPersonaId) && !initializationComplete) {
      // Set loading state
      setIsRemixLoading(true);
      setLoadingMessage(`Preparing to remix persona...`);
      
      const id = personaId || remixPersonaId;
      
      // First, get the persona name
      personaService.getPersona(id)
        .then(persona => {
          setLoadingMessage(`Remixing persona "${persona.name}"...`);
          
          // Now remix the persona
          return remixPersona(id);
        })
        .then(() => {
          setShowIntentForm(false);
          setInitializationComplete(true);
          setIsRemixLoading(false);
        })
        .catch((err) => {
          console.error('Error remixing persona:', err);
          // Fallback to ID if we couldn't get the name
          setLoadingMessage(`Remixing persona ${id}...`);
          setInitializationComplete(true);
          setIsRemixLoading(false);
        });
    } else if (isEditMode && personaId && !initializationComplete) {
      // Set loading state for edit
      setIsRemixLoading(true);
      setLoadingMessage(`Preparing to edit persona...`);
      
      console.log(`Loading persona for editing: ${personaId}`);
      
      // First, get the persona name
      personaService.getPersona(personaId)
        .then(persona => {
          setLoadingMessage(`Loading persona "${persona.name}" for editing...`);
          
          // Now load the persona for editing
          return loadPersona(personaId);
        })
        .then(() => {
          setShowIntentForm(false);
          setInitializationComplete(true);
          setIsRemixLoading(false);
        })
        .catch((err) => {
          console.error('Error loading persona:', err);
          // Fallback to ID if we couldn't get the name
          setLoadingMessage(`Loading persona ${personaId} for editing...`);
          setInitializationComplete(true);
          setIsRemixLoading(false);
        });
    }
  }, [isRemixMode, isEditMode, personaId, remixPersona, loadPersona, initializationComplete]);

  // Draft mode: pre-fill intent from Draft Board. The reset effect above has
  // already cleared the store; this effect just hydrates the local intent form.
  useEffect(() => {
    if (!isDraftMode || !draftId || initializationComplete) return;

    const loadDraft = async () => {
      try {
        const draft = await draftBoardService.get(draftId);
        setPreflightIntent(draft.intent_text);
        setIntent(draft.intent_text);
      } catch (err) {
        console.error('Error loading draft:', err);
      }
      setInitializationComplete(true);
    };
    loadDraft();
  }, [isDraftMode, draftId, initializationComplete]);

  // Add beforeunload event listener for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && !autoSaveEnabled) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, autoSaveEnabled]);

  // Fire autosave on component unmount if dirty and autosave is on
  useEffect(() => {
    return () => {
      const { isDirty, autoSaveEnabled, currentPersonaId, isLoading } =
        useFabricationStore.getState();
      if (autoSaveEnabled && isDirty && currentPersonaId && !isLoading) {
        useFabricationStore.getState().triggerAutoSave();
      }
    };
  }, []);
  
  const handleSubmitIntent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (intent.trim()) {
      // Set loading state
      setIsRemixLoading(true);
      setLoadingMessage('Creating new persona from intent...');
      
      try {
        await init(intent);
        setShowIntentForm(false);
        
        // Clean up the URL if needed
        if (isNewFromURL) {
          navigate('/personas/forge', { replace: true });
        }
      } catch (err) {
        console.error('Error creating persona:', err);
      } finally {
        setIsRemixLoading(false);
      }
    }
  };
  
  const handleNewPersona = () => {
    if (isDirty) {
      setShowNewConfirmation(true);
    } else {
      resetForgeState();
    }
  };
  
  // Get auto-generate flag from the store (currentPersonaId destructured above).
  const { hasAutoGeneratedControls } = useFabricationStore();

  // Compute which tabs are unlocked based on current state
  const tabPrerequisites = React.useMemo(() => {
    if (!state) return [false, false, false, false, false, false, false, false];
    const hasGenesis  = !!state.character_bible?.trim();
    const hasArtifact = !!state.system_prompt;
    return [
      true,                               // 0 Source  — always available
      hasGenesis,                         // 1 Tuning   — needs character bible
      hasGenesis,                         // 2 Artifact — needs character bible
      !!currentPersonaId && hasArtifact,  // 3 Probes   — needs save + compiled artifact
      hasArtifact,                        // 4 Capabilities — needs compiled artifact
      hasGenesis,                         // 5 Map      — needs character bible (same as Tuning)
      !!currentPersonaId,                 // 6 Skills   — needs saved persona
      !!currentPersonaId,                 // 7 Lineage  — needs saved persona
    ];
  }, [state, currentPersonaId]);
  
  // Handle save persona
  const handleSavePersona = async (
    name: string, 
    description: string, 
    isPublic: boolean,
    forceNew: boolean
  ) => {
    await useFabricationStore.getState().savePersona(name, description, isPublic, forceNew);
    setShowSaveDialog(false);
    saveIntentRef.current = null;
    setSaveToast(true);
    setTimeout(() => setSaveToast(false), 3000);
  };

  // Open save dialog — intercept if recompile is needed
  const openSaveDialog = () => {
    if (needs_recompile) {
      setShowRecompilePrompt(true);
      return;
    }
    setShowSaveDialog(true);
  };

  // Tab switch intercept
  const handleSetActivePanel = (index: number) => {
    // Gate: tab not yet unlocked — silently block
    if (!tabPrerequisites[index]) return;

    // Gate: probes require a saved persona
    if (index === 3 && !currentPersonaId) {
      setShowSaveBeforeProbesModal(true);
      return;
    }

    // Read store state at call time — never use React closure values for guards
    const fresh = useFabricationStore.getState();

    // Autosave on tab switch (fire-and-forget, non-blocking)
    if (fresh.autoSaveEnabled && fresh.isDirty && fresh.currentPersonaId) {
      fresh.triggerAutoSave();
    }

    fresh.setActivePanel(index);

    // Hard gate: operation in flight — don't trigger more work
    if (fresh.isLoading) return;

    // Auto-compile: only on Artifact tab, only when stale
    if (index === 2 && fresh.auto_mode && fresh.needs_recompile) {
      fresh.runCompile();
    }

    // Auto-generate controls: only on first Tuning visit with a character bible
    if (index === 1 && !fresh.hasAutoGeneratedControls && fresh.state?.character_bible) {
      fresh.generateControls();
    }
  };

  // Determine which panel to show based on stage and active_panel_index
  const getActivePanel = () => {
    if (!state) return null;
    
    // If a specific panel is selected, show it regardless of stage
    if (state.active_panel_index === 0) return <SourcePanel />;
    if (state.active_panel_index === 1) return <CockpitPanel />;
    if (state.active_panel_index === 2) return <ArtifactPanel openSaveDialog={openSaveDialog} />;
    if (state.active_panel_index === 3) return <ProbesPanel />;
    if (state.active_panel_index === 4) return <CapabilitiesPanel />;
    if (state.active_panel_index === 5) return <MindMapPanel />;
    if (state.active_panel_index === 6) return <SkillsPanel />;
    if (state.active_panel_index === 7) return <LineagePanel />;

    // Otherwise, show the panel corresponding to the current stage
    switch (state.stage) {
      case FabricationStage.GENESIS:
        return <SourcePanel />;
      case FabricationStage.TUNING:
        return <CockpitPanel />;
      case FabricationStage.VERIFICATION:
        return <ArtifactPanel openSaveDialog={openSaveDialog} />;
      default:
        return <SourcePanel />;
    }
  };
  
  // Render a simple loading screen
  const renderLoadingScreen = () => (
    <div className={`p-6 rounded-lg shadow-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="flex flex-col items-center justify-center space-y-4">
        <Spinner size="lg" />
        <h2 className="text-xl font-semibold">{loadingMessage}</h2>
        <div className="w-full max-w-md bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
          <div className="bg-blue-600 h-2.5 rounded-full animate-pulse" style={{ width: '100%' }}></div>
        </div>
      </div>
    </div>
  );
  
  return (
    <div className={`w-full ${darkMode ? 'text-white' : 'text-gray-800'}`}>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <div className={`mr-3 p-2 rounded-lg ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'}`}>
            <FiCode className={`h-6 w-6 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`} />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-blue-300 border-l-4 border-blue-700 pl-3' : 'text-blue-700 border-l-4 border-blue-500 pl-3'}`}>
              Persona Forge {isRemixMode
                ? '(Remixing Persona)'
                : isEditMode
                  ? '(Editing Persona)'
                  : isDraftMode
                    ? '(Forging from Draft)'
                    : '(Creating New Persona)'}
            </h1>
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Engineer specialized AI personas with precision
            </p>
          </div>
        </div>
        
        <div className="flex space-x-2">
          {/* New Persona button - enhanced */}
          {state && (
            <button
              onClick={handleNewPersona}
              className={`px-4 py-2 rounded-md flex items-center transition-colors ${
                darkMode 
                  ? 'bg-green-600 hover:bg-green-700 text-white shadow-sm' 
                  : 'bg-green-500 hover:bg-green-600 text-white shadow-sm'
              }`}
            >
              <FiPlus className="mr-1.5" />
              New Persona
            </button>
          )}
          
          {/* Back to Personas button - enhanced */}
          <button
            onClick={() => {
              if (isDirty) {
                setShowNavigationConfirmation(true);
              } else {
                navigate('/personas');
              }
            }}
            className={`px-4 py-2 rounded-md flex items-center transition-colors ${
              darkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-white shadow-sm' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700 shadow-sm'
            }`}
          >
            <FiArrowLeft className="mr-1.5" />
            Back to Personas
          </button>
        </div>
      </div>
      
      {error && (
        <div className={`p-4 mb-4 rounded-md ${darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'}`}>
          {error}
        </div>
      )}
      
      {/* Show remix indicator if in remix mode */}
      {isRemixMode && state && state.origin_id && (
        <div className={`p-4 mb-4 rounded-md ${
          darkMode ? 'bg-indigo-900 bg-opacity-30 border border-indigo-800' : 'bg-indigo-50 border border-indigo-200'
        }`}>
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
            </svg>
            <span className={darkMode ? 'text-indigo-300' : 'text-indigo-700'}>
              Creating a remix. Changes will be saved as a new persona.
            </span>
          </div>
        </div>
      )}
      
      {isRemixLoading ? (
        renderLoadingScreen()
      ) : showIntentForm && !state ? (
        <div className={`p-6 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800 border-l-4 border-blue-700' : 'bg-white border-l-4 border-blue-500'}`}>
          {preflightIntent && !state && (
            <div className="mb-4 p-3 bg-rose-50 border-l-4 border-rose-500 rounded">
              <p className="text-sm text-gray-600">From your Draft Board:</p>
              <p className="font-medium text-gray-900 mt-1">"{preflightIntent}"</p>
              <Link to="/personas/draft-board" className="text-rose-600 text-sm mt-1 inline-block hover:underline">
                ← Back to drafts
              </Link>
            </div>
          )}
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <div className={`mr-3 p-2 rounded-lg ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'}`}>
              <FiCode className={`h-5 w-5 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`} />
            </div>
            Create New Persona
          </h2>
          <form onSubmit={handleSubmitIntent}>
            <div className="mb-4">
              <label className={`block mb-2 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Describe your persona intent:
              </label>
              <textarea
                className={`w-full p-3 rounded-md ${
                  darkMode 
                    ? 'bg-gray-700 text-white border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500' 
                    : 'bg-gray-100 text-gray-800 border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                } border transition-colors`}
                rows={4}
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
                onKeyDown={(e) => {
                  // Submit on Ctrl+Enter or Cmd+Enter
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                    e.preventDefault();
                    if (intent.trim()) {
                      handleSubmitIntent(e as unknown as React.FormEvent);
                    }
                  }
                }}
                placeholder="e.g., Create a helpful AI assistant that specializes in creative writing and storytelling..."
                required
              />
              <p className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Be specific about the persona's expertise, personality traits, and intended use cases.
              </p>
            </div>
            <button
              type="submit"
              className={`px-4 py-2 rounded-md ${
                darkMode 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white shadow-md'
              } transition-colors flex items-center`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <FiCode className="mr-2" />
                  Generate Persona
                </>
              )}
            </button>
          </form>
        </div>
      ) : state ? (
        <div>
          {/* ForgeHeader */}
          <ForgeHeader onSave={openSaveDialog} onStageClick={handleSetActivePanel} />
          
          {/* Stage Navigation - Enhanced with Admin Dashboard styling */}
          <div className="flex mb-6 border-b border-gray-700 overflow-x-auto">
            {/* Source — always available */}
            <button
              onClick={() => handleSetActivePanel(0)}
              disabled={!tabPrerequisites[0]}
              title="Source"
              className={`px-4 py-3 mr-2 rounded-t-md flex items-center transition-all ${
                state.active_panel_index === 0
                  ? darkMode
                    ? 'bg-blue-900 text-blue-300 border-t-2 border-l-2 border-r-2 border-blue-700 shadow-[0_0_10px_rgba(59,130,246,0.5)]'
                    : 'bg-blue-500 text-white border-t-2 border-l-2 border-r-2 border-blue-600 shadow-md'
                  : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              <FiCode className={`mr-2 ${state.active_panel_index === 0 ? 'animate-pulse' : ''}`} />
              <span>Source</span>
              {!state.character_bible?.length && (
                <span className="ml-2 text-amber-400 text-xs">●</span>
              )}
            </button>

            {/* Tuning — needs character bible */}
            <button
              onClick={() => handleSetActivePanel(1)}
              disabled={!tabPrerequisites[1]}
              title={!tabPrerequisites[1] ? 'Write your character bible in Source first' : 'Tuning'}
              className={`px-4 py-3 mr-2 rounded-t-md flex items-center transition-all ${
                !tabPrerequisites[1]
                  ? 'opacity-40 cursor-not-allowed bg-gray-700 text-gray-500'
                  : state.active_panel_index === 1
                    ? darkMode
                      ? 'bg-purple-900 text-purple-300 border-t-2 border-l-2 border-r-2 border-purple-700 shadow-[0_0_10px_rgba(147,51,234,0.5)]'
                      : 'bg-purple-500 text-white border-t-2 border-l-2 border-r-2 border-purple-600 shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              <FiSliders className={`mr-2 ${state.active_panel_index === 1 ? 'animate-pulse' : ''}`} />
              <span>Tuning</span>
              {tabPrerequisites[1] && !state.ui_schema?.length && (
                <span className="ml-2 text-amber-400 text-xs">●</span>
              )}
            </button>

            {/* Artifact — needs character bible */}
            <button
              onClick={() => handleSetActivePanel(2)}
              disabled={!tabPrerequisites[2]}
              title={!tabPrerequisites[2] ? 'Write your character bible in Source first' : 'Artifact'}
              className={`px-4 py-3 mr-2 rounded-t-md flex items-center transition-all ${
                !tabPrerequisites[2]
                  ? 'opacity-40 cursor-not-allowed bg-gray-700 text-gray-500'
                  : state.active_panel_index === 2
                    ? darkMode
                      ? 'bg-green-900 text-green-300 border-t-2 border-l-2 border-r-2 border-green-700 shadow-[0_0_10px_rgba(34,197,94,0.5)]'
                      : 'bg-green-500 text-white border-t-2 border-l-2 border-r-2 border-green-600 shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              <FiFileText className={`mr-2 ${state.active_panel_index === 2 ? 'animate-pulse' : ''}`} />
              <span>Artifact</span>
              {tabPrerequisites[2] && needs_recompile && (
                <span className="ml-2 text-amber-400 text-xs">●</span>
              )}
            </button>

            {/* Probes — needs save + compiled artifact */}
            <button
              onClick={() => handleSetActivePanel(3)}
              disabled={!tabPrerequisites[3]}
              title={!tabPrerequisites[3] ? 'Compile your artifact and save the persona first' : 'Probes'}
              className={`px-4 py-3 rounded-t-md flex items-center transition-all ${
                !tabPrerequisites[3]
                  ? 'opacity-40 cursor-not-allowed bg-gray-700 text-gray-500'
                  : state.active_panel_index === 3
                    ? darkMode
                      ? 'bg-amber-900 text-amber-300 border-t-2 border-l-2 border-r-2 border-amber-700 shadow-[0_0_10px_rgba(245,158,11,0.5)]'
                      : 'bg-amber-500 text-white border-t-2 border-l-2 border-r-2 border-amber-600 shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              <FiTarget className={`mr-2 ${state.active_panel_index === 3 ? 'animate-pulse' : ''}`} />
              <span>Probes</span>
              <span className="ml-1.5 px-1 py-0.5 rounded text-xs font-bold bg-amber-500 text-white leading-none">BETA</span>
              {tabPrerequisites[3] && state.probes?.length > 0 && (
                <span className="ml-2 text-xs opacity-70">({state.probes.length})</span>
              )}
            </button>

            {/* Capabilities — needs compiled artifact */}
            <button
              onClick={() => handleSetActivePanel(4)}
              disabled={!tabPrerequisites[4]}
              title={!tabPrerequisites[4] ? 'Compile your artifact first' : 'Capabilities'}
              className={`px-4 py-3 ml-2 rounded-t-md flex items-center transition-all ${
                !tabPrerequisites[4]
                  ? 'opacity-40 cursor-not-allowed bg-gray-700 text-gray-500'
                  : state.active_panel_index === 4
                    ? darkMode
                      ? 'bg-cyan-900 text-cyan-300 border-t-2 border-l-2 border-r-2 border-cyan-700 shadow-[0_0_10px_rgba(6,182,212,0.5)]'
                      : 'bg-cyan-500 text-white border-t-2 border-l-2 border-r-2 border-cyan-600 shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              <FiZap className={`mr-2 ${state.active_panel_index === 4 ? 'animate-pulse' : ''}`} />
              <span>Capabilities</span>
              <span className="ml-1.5 px-1 py-0.5 rounded text-xs font-bold bg-amber-500 text-white leading-none">BETA</span>
              {tabPrerequisites[4] && Object.keys(state.capabilities || {}).length > 0 && (
                <span className="ml-2 text-xs opacity-70">
                  ({Object.keys(state.capabilities!).length})
                </span>
              )}
            </button>

            {/* Map — needs character bible */}
            <button
              onClick={() => handleSetActivePanel(5)}
              disabled={!tabPrerequisites[5]}
              title={!tabPrerequisites[5] ? 'Write your character bible in Source first' : 'Mind Map'}
              className={`px-4 py-3 ml-2 rounded-t-md flex items-center transition-all ${
                !tabPrerequisites[5]
                  ? 'opacity-40 cursor-not-allowed bg-gray-700 text-gray-500'
                  : state.active_panel_index === 5
                    ? darkMode
                      ? 'bg-teal-900 text-teal-300 border-t-2 border-l-2 border-r-2 border-teal-700 shadow-[0_0_10px_rgba(20,184,166,0.5)]'
                      : 'bg-teal-500 text-white border-t-2 border-l-2 border-r-2 border-teal-600 shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              <FiMap className={`mr-2 ${state.active_panel_index === 5 ? 'animate-pulse' : ''}`} />
              <span>Map</span>
              <span className="ml-1.5 px-1 py-0.5 rounded text-xs font-bold bg-amber-500 text-white leading-none flex items-center gap-0.5">
                🏗 BETA
              </span>
            </button>

            {/* Skills — disabled for now */}
            {/*
            <button
              onClick={() => handleSetActivePanel(6)}
              disabled={!tabPrerequisites[6]}
              title={!tabPrerequisites[6] ? 'Save the persona first to attach skills' : 'Skills'}
              className={`px-4 py-3 ml-2 rounded-t-md flex items-center transition-all ${
                !tabPrerequisites[6]
                  ? 'opacity-40 cursor-not-allowed bg-gray-700 text-gray-500'
                  : state.active_panel_index === 6
                    ? darkMode
                      ? 'bg-rose-900 text-rose-300 border-t-2 border-l-2 border-r-2 border-rose-700 shadow-[0_0_10px_rgba(244,63,94,0.5)]'
                      : 'bg-rose-500 text-white border-t-2 border-l-2 border-r-2 border-rose-600 shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              <FiBookOpen className={`mr-2 ${state.active_panel_index === 6 ? 'animate-pulse' : ''}`} />
              <span>Skills</span>
            </button>
            */}

            {/* Heritage Tab (index 7) */}
            <button
              disabled={!tabPrerequisites[7]}
              onClick={() => handleSetActivePanel(7)}
              title={!tabPrerequisites[7] ? 'Save the persona first to view heritage' : 'Heritage'}
              className={`px-4 py-3 ml-2 rounded-t-md flex items-center transition-all ${
                !tabPrerequisites[7]
                  ? 'opacity-40 cursor-not-allowed bg-gray-700 text-gray-500'
                  : state.active_panel_index === 7
                    ? darkMode
                      ? 'bg-violet-900 text-violet-300 border-t-2 border-l-2 border-r-2 border-violet-700 shadow-[0_0_10px_rgba(139,92,246,0.5)]'
                      : 'bg-violet-500 text-white border-t-2 border-l-2 border-r-2 border-violet-600 shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              <FiGitBranch className={`mr-2 ${state.active_panel_index === 7 ? 'animate-pulse' : ''}`} />
              <span>Heritage</span>
            </button>
          </div>
          
          {/* Active Panel - Enhanced with admin-style container */}
          <div className={`rounded-lg shadow-lg p-6 ${
            darkMode
              ? state.active_panel_index === 0
                ? 'bg-gray-800 border-l-4 border-blue-700'
                : state.active_panel_index === 1
                  ? 'bg-gray-800 border-l-4 border-purple-700'
                  : state.active_panel_index === 2
                    ? 'bg-gray-800 border-l-4 border-green-700'
                    : state.active_panel_index === 4
                      ? 'bg-gray-800 border-l-4 border-cyan-700'
                      : state.active_panel_index === 5
                        ? 'bg-gray-800 border-l-4 border-teal-700'
                        : state.active_panel_index === 6
                          ? 'bg-gray-800 border-l-4 border-rose-700'
                          : state.active_panel_index === 7
                            ? 'bg-gray-800 border-l-4 border-violet-700'
                            : 'bg-gray-800 border-l-4 border-amber-600'
              : state.active_panel_index === 0
                ? 'bg-white border-l-4 border-blue-500'
                : state.active_panel_index === 1
                  ? 'bg-white border-l-4 border-purple-500'
                  : state.active_panel_index === 2
                    ? 'bg-white border-l-4 border-green-500'
                    : state.active_panel_index === 4
                      ? 'bg-white border-l-4 border-cyan-500'
                      : state.active_panel_index === 5
                        ? 'bg-white border-l-4 border-teal-500'
                        : state.active_panel_index === 6
                          ? 'bg-white border-l-4 border-rose-500'
                          : state.active_panel_index === 7
                            ? 'bg-white border-l-4 border-violet-500'
                            : 'bg-white border-l-4 border-amber-500'
          }`}>
            {getActivePanel()}
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center h-64">
          <Spinner size="lg" />
        </div>
      )}
      
      {/* Recompile Required Dialog */}
      {showRecompilePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} w-[460px] max-w-full`}>
            <h3 className="text-lg font-semibold mb-2">Recompile Required</h3>
            <p className={`text-sm mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Your source or controls have changed since the last compile.
              The saved artifact will be stale unless you recompile first.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRecompilePrompt(false)}
                className={`px-4 py-2 rounded-md text-sm ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowRecompilePrompt(false);
                  setShowSaveDialog(true);
                }}
                className={`px-4 py-2 rounded-md text-sm ${
                  darkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-400 hover:bg-gray-500 text-white'
                }`}
              >
                Save Anyway
              </button>
              <button
                onClick={() => {
                  setShowRecompilePrompt(false);
                  handleSetActivePanel(2);
                  runCompile();
                }}
                className={`px-4 py-2 rounded-md text-sm text-white ${
                  darkMode ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-500 hover:bg-blue-600'
                }`}
              >
                Compile Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Blocker Modal */}
      {showNavBlockerModal && blocker.state === 'blocked' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} w-[480px] max-w-full`}>
            <h3 className="text-lg font-semibold mb-2">Unsaved Changes</h3>
            <p className={`text-sm mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              You have unsaved changes to this persona. What would you like to do before leaving?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowNavBlockerModal(false);
                  blocker.reset();
                }}
                className={`px-4 py-2 rounded-md text-sm ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowNavBlockerModal(false);
                  blocker.proceed();
                }}
                className={`px-4 py-2 rounded-md text-sm text-white ${
                  darkMode ? 'bg-red-700 hover:bg-red-600' : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                Discard & Leave
              </button>
              <button
                onClick={() => {
                  setShowNavBlockerModal(false);
                  blocker.reset();
                  openSaveDialog();
                }}
                className={`px-4 py-2 rounded-md text-sm text-white ${
                  darkMode ? 'bg-green-600 hover:bg-green-500' : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                Save First
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Persona Confirmation Dialog */}
      {showNewConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} w-[500px] max-w-full`}>
            <h3 className="text-lg font-semibold mb-4">Unsaved Changes</h3>
            <p className="mb-4">You have unsaved changes. Are you sure you want to create a new persona?</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowNewConfirmation(false)}
                className={`px-4 py-2 rounded-md ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  resetForgeState();
                  setShowNewConfirmation(false);
                }}
                className={`px-4 py-2 rounded-md ${
                  darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'
                } text-white`}
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Confirmation Dialog */}
      {showNavigationConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} w-[500px] max-w-full`}>
            <h3 className="text-lg font-semibold mb-4">Unsaved Changes</h3>
            <p className="mb-4">You have unsaved changes. Are you sure you want to leave?</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowNavigationConfirmation(false)}
                className={`px-4 py-2 rounded-md ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  navigate('/personas');
                }}
                className={`px-4 py-2 rounded-md ${
                  darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'
                } text-white`}
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Save Before Probes Modal */}
      {showSaveBeforeProbesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} w-[480px] max-w-full`}>
            <h3 className="text-lg font-semibold mb-2">💾 Save Required for Probes</h3>
            <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Probes are generated against the <strong>saved version</strong> of your persona in the database.
              You need to save first so the probe engine can read your compiled artifact.
            </p>
            <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              After saving, you'll be taken directly to the Probes tab.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowSaveBeforeProbesModal(false)}
                className={`px-4 py-2 rounded-md text-sm ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowSaveBeforeProbesModal(false);
                  saveIntentRef.current = 'probes';
                  openSaveDialog();
                }}
                className={`px-4 py-2 rounded-md text-sm text-white ${
                  darkMode ? 'bg-green-600 hover:bg-green-500' : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                Save Now →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Dialog */}
      <SaveDialog
        ref={saveDialogRef}
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        onSave={handleSavePersona}
        isLoading={isLoading}
        initialName={state?.display_name || ''}
        initialDescription={state?.description || ''}
        currentPersonaId={currentPersonaId}
        suggestedNames={state?.suggested_names || []}
      />

      {/* TestDrivePanel — mounted outside tab container, persists across tab switches */}
      {state && (
        <TestDrivePanel
          systemPrompt={state.system_prompt || null}
          personaName={state.display_name || 'Persona'}
          needsRecompile={needs_recompile}
          onCompile={runCompile}
        />
      )}

      {/* Save toast */}
      {saveToast && (
        <div className={`fixed bottom-20 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 transition-all ${
          darkMode
            ? 'bg-green-800 text-green-100'
            : 'bg-green-500 text-white'
        }`}>
          <span>✓</span>
          <span>
            {currentPersonaId
              ? 'Persona updated successfully'
              : 'Persona saved successfully'}
          </span>
        </div>
      )}
    </div>
  );
};

export default PersonaForge;
